import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, LoaderCircleIcon, XIcon } from 'lucide-react';
import { lenderNames } from '../../data/withdraw';
import { WithdrawFlow } from '../../hooks/useWithdrawFlow';
import { easeOut } from '../../utils/styles';
import { isReApp } from '../../utils/withdrawValidation';
import { PillButton } from '../ui/PillButton';
import { DeadPathStep } from './DeadPathStep';
import { LoanHeader } from './LoanHeader';
import { PathChoiceStep } from './PathChoiceStep';
import { SeasoningPathStep } from './SeasoningPathStep';
import { StepProgress } from './StepProgress';
import { SuccessStep } from './SuccessStep';
interface WithdrawModalProps {
  flow: WithdrawFlow;
}
export function WithdrawModal({
  flow
}: WithdrawModalProps) {
  const {
    screen,
    step,
    direction,
    path,
    canContinue,
    submitting
  } = flow;
  const reduce = useReducedMotion();
  const offset = reduce ? 0 : 28;
  const reApp = path === 'dead' && step > 0 && isReApp(flow.dead);
  const continueLabel = step === 0 ? 'Continue' : path === 'dead' ? reApp ? 'Withdraw and Re-App' : 'Withdraw loan' : 'Enroll in seasoning';
  const lender = path === 'dead' && flow.dead.lenderVariant === 'nonsync' ? lenderNames.nonsync : lenderNames.sync;
  return <section role="dialog" aria-modal="true" aria-labelledby="withdraw-title" className="w-full max-w-[720px] overflow-hidden rounded-[28px] bg-white shadow-modal ring-1 ring-slate-900/5" data-id="element-232" data-mp="2mowuaw5q377x">
      <header className="border-b border-slate-100 bg-gradient-to-b from-primary-50 via-white to-white px-6 pb-6 pt-6 sm:px-8" data-id="element-233" data-mp="2wkgbsdjji3d5">
        <div className="flex items-start justify-between gap-4" data-id="element-234" data-mp="35mzaqmk7fvu4">
          <div data-id="element-235" data-mp="35m6dqdb1skp6">
            <h2 id="withdraw-title" className="text-xl font-bold tracking-tight text-slate-900" data-id="element-236" data-mp="35kv785q7wyud">
              {reApp ? 'Withdraw and Re-App' : 'Withdraw Loan'}
            </h2>
            <p className="mt-1 text-sm text-slate-500" data-id="element-237" data-mp="1b9ggpmpduvdk">Choose how this file leaves the active pipeline.</p>
          </div>
          <button type="button" aria-label="Close" onClick={flow.reset} className="grid h-9 w-9 place-items-center rounded-full text-slate-400 transition-colors duration-150 hover:bg-white hover:text-slate-700 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-slate-200" data-id="element-238" data-mp="2eloz9vnuon56">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-239" data-mp="2xhohwfuxuec3" />
          </button>
        </div>
        <LoanHeader lender={lender} className="mt-5" data-id="element-240" data-mp="2skex5dihuqul" />
        <StepProgress step={step} className="mt-6" data-id="element-241" data-mp="2shiuxczm5g0c" />
      </header>

      <div className="relative min-h-[380px] px-6 py-7 sm:px-8" data-id="element-242" data-mp="377849vhv0qbp">
        <AnimatePresence mode="wait" initial={false} custom={direction} data-id="element-243" data-mp="328wt9hftw9rw">
          <motion.div key={screen} custom={direction} variants={{
          enter: (d: number) => ({
            x: d * offset,
            opacity: 0
          }),
          center: {
            x: 0,
            opacity: 1
          },
          exit: (d: number) => ({
            x: -d * offset,
            opacity: 0
          })
        }} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.22,
          ease: easeOut
        }} data-id="element-244" data-mp="32bssra7br6ig">
            {screen === 'path' && <PathChoiceStep value={path} onChange={flow.setPath} data-id="element-245" data-mp="3luyoyumud6dj" />}
            {screen === 'dead' && <DeadPathStep form={flow.dead} onChange={flow.updateDead} data-id="element-246" data-mp="3pv8te2uzjoqa" />}
            {screen === 'seasoning' && <SeasoningPathStep form={flow.seasoning} onChange={flow.updateSeasoning} data-id="element-247" data-mp="3c3ub5y8wjrka" />}
            {screen === 'success' && <SuccessStep path={path ?? 'dead'} dead={flow.dead} seasoning={flow.seasoning} onDone={flow.reset} data-id="element-248" data-mp="3gxyjaktwm5my" />}
          </motion.div>
        </AnimatePresence>
      </div>

      {screen !== 'success' && <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/70 px-6 py-4 sm:px-8" data-id="element-249" data-mp="2t8x5ei2ay2aa">
          <PillButton variant="ghost" onClick={flow.reset} data-id="element-250" data-mp="2q57sgmqkkj73">
            Cancel
          </PillButton>
          <div className="flex items-center gap-3" data-id="element-251" data-mp="2l6whg8ojg2na">
            {step === 1 && !canContinue && <span className="hidden text-xs text-slate-500 md:inline" data-id="element-252" data-mp="2ge3cbjp5qsao">Fill in the required fields to continue</span>}
            {step > 0 && <PillButton variant="secondary" onClick={flow.back} disabled={submitting} icon={<ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-254" data-mp="27cdlaez6tvdf" />} data-id="element-253" data-mp="27eglfru214vv">
                Back
              </PillButton>}
            <PillButton variant={step === 1 && path === 'dead' ? 'danger' : 'primary'} onClick={flow.next} disabled={!canContinue || submitting} aria-busy={submitting} icon={submitting ? <LoaderCircleIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-256" data-mp="3rts0ufi27zdl" /> : undefined} iconRight={!submitting && step === 0 ? <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-257" data-mp="19kyuf3gcxyj3" /> : undefined} data-id="element-255" data-mp="1xg0rzi7k1jj7">
              {submitting ? 'Saving…' : continueLabel}
            </PillButton>
          </div>
        </footer>}
    </section>;
}